// Share invite panel — shows the album link, the passcode (view/change), and an
// editable invite message. "Share" opens the native share sheet; "Copy invite"
// copies the full text (message + link + passcode). Custom messages persist on
// the album so they're remembered next time.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X as XIcon, Copy, Share2, Link2, Lock, Eye, EyeOff, Pencil, Check, RotateCcw, Plus,
} from "lucide-react";
import { Albums, getHostPasscodePlain, setHostPasscodePlain } from "../lib/api";
import { defaultShareMessage, albumUrl, buildInviteText } from "../lib/shareInvite";

export default function ShareEventModal({ album, onClose, onUpdated }) {
  const [message, setMessage] = useState(
    (album.share_message || "").trim() || defaultShareMessage(album)
  );
  const [code, setCode] = useState(album.host_passcode || getHostPasscodePlain(album.slug));
  const [showCode, setShowCode] = useState(false);
  const [editingCode, setEditingCode] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [busy, setBusy] = useState(false);
  const url = albumUrl(album.slug);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Persist the custom message (silently) so it's remembered next time.
  const persistMessage = async () => {
    const trimmed = message.trim();
    const toStore = trimmed === defaultShareMessage(album) ? "" : trimmed;
    if (toStore === (album.share_message || "")) return;
    try {
      const updated = await Albums.patch(album.slug, { share_message: toStore });
      onUpdated?.(updated);
    } catch (e) {
      console.warn("share_message save failed", e);
      toast.error("Couldn't save your custom message — it will still be included this time");
    }
  };

  const inviteText = buildInviteText(album, message);

  const copyInvite = async () => {
    persistMessage();
    await navigator.clipboard.writeText(inviteText);
    toast.success(album.requires_passcode ? "Invite copied — link + passcode included" : "Invite copied");
  };

  const shareNative = async () => {
    persistMessage();
    if (navigator.share) {
      try {
        await navigator.share({ title: `${album.name} — mEYEcam`, text: inviteText });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return; // user cancelled — silent
      }
    }
    await navigator.clipboard.writeText(inviteText);
    toast.success("Sharing isn't available here — invite copied instead");
  };

  const saveNewCode = async () => {
    const p = newCode.trim();
    if (!p) return;
    const adding = !album.requires_passcode;
    setBusy(true);
    try {
      const updated = await Albums.patch(album.slug, { passcode: p });
      setHostPasscodePlain(album.slug, p);
      setCode(p);
      setEditingCode(false);
      setNewCode("");
      toast.success(adding ? "Passcode added — it's now part of your invite" : "Passcode changed");
      onUpdated?.(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save passcode");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(5,3,14,0.72)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="share-event-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl p-5 max-h-[90vh] overflow-y-auto"
        style={{
          background: "#15131F",
          border: "1px solid rgba(127,75,255,0.35)",
          boxShadow: "0 30px 80px -20px rgba(0,0,0,0.9)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Share2 size={16} color="#22E2FF" />
            <div className="text-base font-semibold tracking-tight">Share “{album.name}”</div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/5"
            aria-label="Close"
            data-testid="share-modal-close"
          >
            <XIcon size={16} />
          </button>
        </div>

        {/* Link */}
        <div className="text-[11px] font-semibold tracking-wider mb-1.5" style={{ color: "rgba(255,255,255,0.55)" }}>
          LINK
        </div>
        <div
          className="flex items-center gap-2 rounded-xl px-3 py-2.5 mb-3"
          style={{ background: "rgba(127,75,255,0.10)", border: "1px solid rgba(127,75,255,0.32)" }}
        >
          <Link2 size={14} style={{ color: "#7F4BFF" }} />
          <span className="text-sm truncate flex-1" style={{ color: "rgba(255,255,255,0.9)" }}>{url}</span>
          <button
            type="button"
            onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied"); }}
            className="opacity-70 hover:opacity-100"
            aria-label="Copy link only"
            data-testid="share-modal-copy-link"
          >
            <Copy size={14} />
          </button>
        </div>

        {/* Passcode — view/change when set, add when not */}
        <div className="text-[11px] font-semibold tracking-wider mb-1.5" style={{ color: "rgba(255,255,255,0.55)" }}>
          PASSCODE
        </div>
        <div
          className="rounded-xl px-3 py-2.5 mb-3"
          style={{ background: "rgba(255,211,0,0.08)", border: "1px solid rgba(255,211,0,0.4)" }}
          data-testid="share-modal-passcode-row"
        >
          {!editingCode && !album.requires_passcode ? (
            <div className="flex items-center gap-2">
              <Lock size={14} color="#FFD300" />
              <span className="text-sm flex-1" style={{ color: "rgba(255,255,255,0.65)" }}>
                No passcode — anyone with the link can view
              </span>
              <button
                type="button"
                onClick={() => { setEditingCode(true); setNewCode(""); }}
                className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-lg"
                style={{ background: "rgba(255,211,0,0.14)", border: "1px solid rgba(255,211,0,0.45)", color: "#FFE373" }}
                data-testid="share-modal-add-passcode"
              >
                <Plus size={11} /> Add passcode
              </button>
            </div>
          ) : !editingCode ? (
                <div className="flex items-center gap-2">
                  <Lock size={14} color="#FFD300" />
                  <span
                    className="text-sm font-semibold flex-1 truncate"
                    style={{ color: "#FFE373", letterSpacing: showCode ? "normal" : "2px" }}
                    data-testid="share-modal-passcode-value"
                  >
                    {code ? (showCode ? code : "•".repeat(Math.min(code.length, 10))) : "Not saved on this device"}
                  </span>
                  {code && (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowCode((v) => !v)}
                        className="opacity-70 hover:opacity-100"
                        aria-label={showCode ? "Hide passcode" : "Show passcode"}
                        data-testid="share-modal-toggle-passcode"
                      >
                        {showCode ? <EyeOff size={14} color="#FFE373" /> : <Eye size={14} color="#FFE373" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(code); toast.success("Passcode copied"); }}
                        className="opacity-70 hover:opacity-100"
                        aria-label="Copy passcode"
                        data-testid="share-modal-copy-passcode"
                      >
                        <Copy size={14} color="#FFE373" />
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => { setEditingCode(true); setNewCode(code || ""); }}
                    className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-lg"
                    style={{ background: "rgba(255,211,0,0.14)", border: "1px solid rgba(255,211,0,0.45)", color: "#FFE373" }}
                    data-testid="share-modal-change-passcode"
                  >
                    <Pencil size={11} /> Change
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    autoFocus
                    type="text"
                    className="meye-input flex-1"
                    style={{ padding: "8px 10px", fontSize: 13 }}
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value)}
                    placeholder="New passcode"
                    maxLength={80}
                    autoComplete="off"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveNewCode();
                      if (e.key === "Escape") { setEditingCode(false); setNewCode(""); }
                    }}
                    data-testid="share-modal-passcode-input"
                  />
                  <button
                    type="button"
                    onClick={saveNewCode}
                    disabled={busy || !newCode.trim()}
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: "rgba(34,255,183,0.14)", border: "1px solid rgba(34,255,183,0.45)", opacity: !newCode.trim() ? 0.5 : 1 }}
                    aria-label="Save passcode"
                    data-testid="share-modal-passcode-save"
                  >
                    {busy ? <div className="meye-spinner" style={{ width: 12, height: 12 }} /> : <Check size={14} color="#22FFB7" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEditingCode(false); setNewCode(""); }}
                    className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/5"
                    aria-label="Cancel"
                    data-testid="share-modal-passcode-cancel"
                  >
                    <XIcon size={14} />
                  </button>
                </div>
              )}
        </div>

        {/* Message */}
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[11px] font-semibold tracking-wider" style={{ color: "rgba(255,255,255,0.55)" }}>
            YOUR MESSAGE
          </div>
          <button
            type="button"
            onClick={() => setMessage(defaultShareMessage(album))}
            className="inline-flex items-center gap-1 text-[11px] opacity-70 hover:opacity-100"
            style={{ color: "#A8F2FF" }}
            data-testid="share-modal-reset-message"
          >
            <RotateCcw size={10} /> Reset to default
          </button>
        </div>
        <textarea
          className="meye-input mb-3"
          style={{ minHeight: 76, resize: "vertical", fontSize: 13, lineHeight: 1.5 }}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={300}
          data-testid="share-modal-message-input"
        />

        {/* Preview of exactly what gets sent */}
        <div className="text-[11px] font-semibold tracking-wider mb-1.5" style={{ color: "rgba(255,255,255,0.55)" }}>
          PREVIEW
        </div>
        <div
          className="rounded-xl px-3 py-2.5 mb-4 text-[12px] whitespace-pre-wrap"
          style={{ background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.75)", lineHeight: 1.55 }}
          data-testid="share-modal-preview"
        >
          {inviteText}
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={copyInvite}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition"
            style={{ background: "rgba(127,75,255,0.16)", border: "1px solid rgba(127,75,255,0.45)", color: "#C9B6FF" }}
            data-testid="share-modal-copy-invite"
          >
            <Copy size={14} /> Copy invite
          </button>
          <button
            type="button"
            onClick={shareNative}
            className="flex-1 cta-pill justify-center"
            data-testid="share-modal-share-btn"
          >
            <Share2 size={14} /> Share
          </button>
        </div>
      </div>
    </div>
  );
}
